import { create } from 'zustand'
import { isPdfFile } from '../lib/is-pdf-file'
import { MAX_FILES } from './constants'
import type { StagedFile, UploadItem } from './types'

interface UploadQueueState {
  isModalOpen: boolean
  modalStagedFiles: StagedFile[]
  items: UploadItem[]

  openModal: () => void
  closeModal: () => void
  addFilesToModal: (files: File[]) => { rejectedOverflow: number }
  removeStagedFile: (id: string) => void
  submitUploads: () => void
  setItemStatus: (id: string, next: UploadItem) => void
  retry: (id: string) => void
  dismissPopup: () => void
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

// Lazy import to avoid circular dependency (store <-> runner).
async function runScheduler(): Promise<void> {
  const { scheduleUploads } = await import('../api/upload-runner')
  scheduleUploads()
}

export const useUploadQueueStore = create<UploadQueueState>((set, get) => ({
  isModalOpen: false,
  modalStagedFiles: [],
  items: [],

  openModal: () => set({ isModalOpen: true }),

  closeModal: () => set({ isModalOpen: false, modalStagedFiles: [] }),

  addFilesToModal: (files) => {
    const current = get().modalStagedFiles
    const capacity = Math.max(0, MAX_FILES - current.length)
    const accepted = files.slice(0, capacity)
    const rejectedOverflow = files.length - accepted.length

    const staged: StagedFile[] = accepted.map((file) => ({
      id: generateId(),
      file,
      isSupported: isPdfFile(file),
    }))

    set({ modalStagedFiles: [...current, ...staged] })
    return { rejectedOverflow }
  },

  removeStagedFile: (id) => {
    set({ modalStagedFiles: get().modalStagedFiles.filter((f) => f.id !== id) })
  },

  submitUploads: () => {
    const supported = get().modalStagedFiles.filter((s) => s.isSupported)
    if (supported.length === 0) {
      set({ isModalOpen: false, modalStagedFiles: [] })
      return
    }

    const newItems: UploadItem[] = supported.map((s) => ({
      id: s.id,
      file: s.file,
      status: 'pending',
    }))

    set({
      items: [...get().items, ...newItems],
      isModalOpen: false,
      modalStagedFiles: [],
    })

    void runScheduler()
  },

  setItemStatus: (id, next) => {
    set({
      items: get().items.map((item) => (item.id === id ? next : item)),
    })
  },

  retry: (id) => {
    set({
      items: get().items.map((item) =>
        item.id === id ? { id: item.id, file: item.file, status: 'pending' } : item,
      ),
    })
    void runScheduler()
  },

  dismissPopup: () => set({ items: [] }),
}))

// --- Selectors ------------------------------------------------------------
// Derived-boolean selectors so subscribers re-render only when the boolean flips,
// not on every item status transition (`rerender-derived-state`).

export function selectHasNonTerminal(state: UploadQueueState): boolean {
  return state.items.some((i) => i.status === 'pending' || i.status === 'uploading')
}

export function selectIsUploadEnabled(state: UploadQueueState): boolean {
  return state.modalStagedFiles.some((s) => s.isSupported)
}

export function selectTotalBatchCount(state: UploadQueueState): number {
  return state.items.length
}

export function selectSuccessCount(state: UploadQueueState): number {
  return state.items.filter((i) => i.status === 'success').length
}

export function selectItems(state: UploadQueueState): readonly UploadItem[] {
  return state.items
}
